/**
 * Channel Toggles Component
 *
 * Switch-based UI for enabling/disabling alert notification channels.
 * When Slack is toggled on, it auto-populates from the org Slack integration
 * (so users don't need to paste the webhook URL for every alert).
 * Email still requires manual input.
 */

import { useState, useEffect } from "react";
import { Mail, MessageSquare, Users } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSlackIntegration } from "@/hooks/useSlackIntegration";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import type { AlertChannels } from "@/types/alerts";

interface ChannelTogglesProps {
  channels: AlertChannels;
  onChange: (channels: AlertChannels) => void;
}

export function ChannelToggles({ channels, onChange }: ChannelTogglesProps) {
  const { data: slackIntegration } = useSlackIntegration();
  const { organization } = useOrganizationContext();
  const slackConfigured = slackIntegration != null && slackIntegration.is_active;
  const teamsConfigured = !!organization?.teams_webhook_url_last_four;

  // Separate toggle state from value truthiness to avoid the "" === falsy bug
  const [isEmailOn, setIsEmailOn] = useState(!!channels.email);
  const [isSlackOn, setIsSlackOn] = useState(!!channels.slack);
  const [isTeamsOn, setIsTeamsOn] = useState(!!channels.teams);

  // Sync toggle state when channels prop changes externally (e.g. loading alert data)
  useEffect(() => {
    setIsEmailOn(!!channels.email);
    setIsSlackOn(!!channels.slack);
    setIsTeamsOn(!!channels.teams);
  }, [channels.email, channels.slack, channels.teams]);

  function handleEmailToggle(checked: boolean) {
    setIsEmailOn(checked);
    onChange({
      ...channels,
      email: checked ? channels.email || "" : null,
    });
  }

  function handleTeamsToggle(checked: boolean) {
    setIsTeamsOn(checked);
    onChange({ ...channels, teams: checked || null });
  }

  function handleSlackToggle(checked: boolean) {
    setIsSlackOn(checked);
    onChange({ ...channels, slack: checked || null });
  }

  return (
    <div className="space-y-4">
      {/* Email Channel */}
      <div className="flex items-start gap-4 rounded-lg border p-4">
        <div className="flex items-center gap-3 min-w-[140px]">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="email-toggle" className="text-sm font-medium cursor-pointer">
            Email
          </Label>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Send alert notifications via email
            </p>
            <Switch
              id="email-toggle"
              checked={isEmailOn}
              onCheckedChange={handleEmailToggle}
            />
          </div>
          {isEmailOn && (
            <Input
              type="email"
              placeholder="alerts@example.com"
              value={channels.email || ""}
              onChange={(e) =>
                onChange({ ...channels, email: e.target.value || null })
              }
            />
          )}
        </div>
      </div>

      {/* Slack Channel */}
      <div className="flex items-start gap-4 rounded-lg border p-4">
        <div className="flex items-center gap-3 min-w-[140px]">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="slack-toggle" className="text-sm font-medium cursor-pointer">
            Slack
          </Label>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {slackConfigured
                ? "Send notifications to your organization's Slack channel"
                : "Configure Slack webhook in Settings first"}
            </p>
            <Switch
              id="slack-toggle"
              checked={isSlackOn}
              onCheckedChange={handleSlackToggle}
              disabled={!slackConfigured}
            />
          </div>
          {isSlackOn && slackConfigured && (
            <p className="text-xs text-muted-foreground">
              Using organization Slack integration
              {slackIntegration?.channel_name && (
                <span className="font-medium"> (#{slackIntegration.channel_name})</span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Teams Channel */}
      <div className="flex items-start gap-4 rounded-lg border p-4">
        <div className="flex items-center gap-3 min-w-[140px]">
          <Users className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="teams-toggle" className="text-sm font-medium cursor-pointer">
            Teams
          </Label>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {teamsConfigured
                ? "Send notifications to your organization's Teams channel"
                : "Configure Teams webhook in Settings first"}
            </p>
            <Switch
              id="teams-toggle"
              checked={isTeamsOn}
              onCheckedChange={handleTeamsToggle}
              disabled={!teamsConfigured}
            />
          </div>
          {isTeamsOn && teamsConfigured && (
            <p className="text-xs text-muted-foreground">
              Using organization Teams webhook (****{organization?.teams_webhook_url_last_four})
            </p>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        At least one notification channel must be enabled.
      </p>
    </div>
  );
}
